import React, { useState, useEffect } from "react";
import { MemorialProvider } from "@/contexts/MemorialContext";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { HeroSection } from "@/components/memorial/HeroSection";
import { Footer } from "@/components/memorial/Footer";
import { MemorialHeader } from "@/components/memorial/MemorialHeader";
import { MemorialTabs } from "@/components/memorial/MemorialTabs";
import { PhotoSlideshow } from "@/components/gallery/components/PhotoSlideshow";
import { MediaViewer } from "@/components/stories/MediaViewer";
import { UnassignedB2BMessage } from "@/components/memorial/UnassignedB2BMessage";
import { buildMemorialDisplayName } from "@/utils/memorialDisplayName";
import { getPublicUrl } from "@/utils/domain";
import { MemorialServiceFooter } from "@/components/memorial/MemorialServiceFooter";
import { ServiceOrderDialog } from "@/components/memorial/ServiceOrderDialog";
import { PURCHASING_ENABLED } from "@/config/features";
import { OrderSuccessDialog } from "@/components/memorial/OrderSuccessDialog";
import { FixRequestDialog } from "@/components/memorial/FixRequestDialog";

import { useMemorialSlideshow } from "@/hooks/useMemorialSlideshow";
import { useOptimizedMemorialData } from "@/hooks/useOptimizedMemorialData";
import { supabase } from "@/integrations/supabase/client";
import { LoadingHeroSection } from "@/components/memorial/LoadingHeroSection";
import { LoadingTabs } from "@/components/memorial/LoadingTabs";
import { MemorialMetaTags } from "@/components/memorial/MemorialMetaTags";
import { DynamicMetaTags } from "@/components/shared/DynamicMetaTags";

const Memorial = () => {
  const navigate = useNavigate();
  const {
    id
  } = useParams<{
    id?: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === 'true';
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("about");
  const [memorialId, setMemorialId] = useState<string | undefined>(id);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMediaUrl, setSelectedMediaUrl] = useState<string | null>(null);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState<number>(0);
  const [showB2BEmailForm, setShowB2BEmailForm] = useState(false);
  const [hasGeolocation, setHasGeolocation] = useState(false);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [selectedServiceType, setSelectedServiceType] = useState<"flowers" | "cleaning">("flowers");
  
  // Order success dialog state
  const [orderSuccessOpen, setOrderSuccessOpen] = useState(false);
  const [successOrderId, setSuccessOrderId] = useState<string | null>(null);

  // B2B Fix Request state
  const [fixDialogOpen, setFixDialogOpen] = useState(false);

  // Biography state management moved to Memorial level for stability
  const [selectedBiographyCategory, setSelectedBiographyCategory] = useState<{ id: string; name: string } | null>(null);

  // Check for order success URL parameters
  useEffect(() => {
    const orderSuccess = searchParams.get('order_success');
    const orderId = searchParams.get('order_id');
    
    if (orderSuccess === 'true' && orderId) {
      setSuccessOrderId(orderId);
      setOrderSuccessOpen(true);
      
      // Clear URL parameters
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('order_success');
      newParams.delete('order_id');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (id) {
      setMemorialId(id);
      setIsLoading(false);
    } else {
      console.error('[Memorial] No memorial identifier provided in URL');
      setIsLoading(false);
    }
  }, [id, searchParams, isPreview, location]);

  // Use optimized data fetching for better performance
  const { data: memorialData, isLoading: isOptimizedLoading } = useOptimizedMemorialData(memorialId);
  
  // Check if memorial is unactivated (user_id is null) and handle B2B vs DTC flow
  // Only do this if not in preview mode
  useEffect(() => {
    const checkMemorialOwnership = async () => {
      if (!isOptimizedLoading && memorialData?.memorial && !isPreview && memorialId) {
        const memorial = memorialData.memorial;
        
        // If memorial is unclaimed (no user linked), check if it's a B2B token
        if (!memorial.is_claimed) {
          const { data: token, error } = await supabase
            .from('memorial_tokens')
            .select('token_type, assigned_email, claimed')
            .eq('memorial_id', memorialId)
            .maybeSingle();

          if (error) {
            console.error('[Memorial] Error checking token type:', error);
            navigate(`/welcome/${memorialId}`, { replace: true });
            return;
          }

          if (token?.token_type === 'b2b' && !token.claimed && !token.assigned_email) {
            // B2B token not yet assigned - show email input form for salesperson
            setShowB2BEmailForm(true);
          } else {
            // B2C or already-assigned B2B - redirect to welcome flow
            navigate(`/welcome/${memorialId}`, { replace: true });
          }
        }
      }
    };

    checkMemorialOwnership();
  }, [memorialData, isOptimizedLoading, isPreview, memorialId, navigate]);
  
  // Use optimized data directly
  const galleryMedia = memorialData?.media || [];
  const isMediaDataLoading = isOptimizedLoading;

  const {
    isSlideshow,
    currentSlide,
    startSlideshow,
    handlePrevious,
    handleNext,
    closeSlideshow
  } = useMemorialSlideshow(galleryMedia);

  // Derive B2B status directly from memorial data (no useEffect/state timing issues)
  const monumentCompanyId = memorialData?.memorial?.monument_company_id ?? null;
  const isB2B = !!monumentCompanyId;
  
  // Check if monument company offers repairs for "Fix Headstone" button visibility
  const [offersRepairs, setOffersRepairs] = useState(false);
  
  useEffect(() => {
    const checkCompanyRepairs = async () => {
      if (!monumentCompanyId) {
        setOffersRepairs(false);
        return;
      }
      
      const { data } = await supabase
        .from('monument_companies_public')
        .select('offers_repairs')
        .eq('id', monumentCompanyId)
        .single();
      
      setOffersRepairs(data?.offers_repairs ?? false);
    };
    
    checkCompanyRepairs();
  }, [monumentCompanyId]);
  
  // Check if memorial has geolocation for service CTA visibility
  useEffect(() => {
    const checkGeolocation = async () => {
      if (!memorialId) return;
      
      const { data } = await supabase
        .from('monument_installations')
        .select('latitude, longitude')
        .eq('memorial_id', memorialId)
        .maybeSingle();
      
      setHasGeolocation(
        data !== null && 
        data.latitude !== null && 
        data.longitude !== null
      );
    };
    
    checkGeolocation();
  }, [memorialId]);

  useEffect(() => {
    const storeMemorialUrl = async () => {
      if (!memorialId) return;
      try {
        const {
          data: memorial,
          error
        } = await supabase.from('memorials').select('memorial_url').eq('id', memorialId).single();
        if (error) {
          console.error('Error fetching memorial:', error);
          return;
        }
        if (!memorial.memorial_url) {
          const directUrl = getPublicUrl(`/memorial/${memorialId}`);
          const {
            error: updateError
          } = await supabase.from('memorials').update({
            memorial_url: directUrl
          }).eq('id', memorialId);
          if (updateError) {
            console.error('Error updating memorial URL:', updateError);
          }
        }
      } catch (err) {
        console.error('Error in storeMemorialUrl:', err);
      }
    };
    storeMemorialUrl();
  }, [memorialId]);

  useEffect(() => {
    // Only run scroll logic on initial page load or navigation from other pages
    // Avoid running when user manually switches tabs
    if (location.state && location.state.scrollToLifeJourney) {
      setActiveTab("biography");
      setTimeout(() => {
        const lifeJourneySection = document.getElementById('life-journey');
        if (lifeJourneySection) {
          lifeJourneySection.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }, 100);
      // Clear the state after handling it to prevent re-triggering
      window.history.replaceState(
        { ...location.state, scrollToLifeJourney: false }, 
        '', 
        window.location.pathname + window.location.search
      );
    } else if (location.hash === '#life-journey' && activeTab !== "biography") {
      // Only scroll if we're not already on the biography tab
      setActiveTab("biography");
      setTimeout(() => {
        const lifeJourneySection = document.getElementById('life-journey');
        if (lifeJourneySection) {
          lifeJourneySection.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location, activeTab]);

  const handleBackToAdmin = () => {
    navigate(`/admin?memorialId=${memorialId}`);
  };

  const handleTabChange = (value: string) => {
    // Store current scroll position
    const currentScrollY = window.scrollY;
    
    setActiveTab(value);
    
    // Restore scroll position after the tab change
    requestAnimationFrame(() => {
      window.scrollTo(0, currentScrollY);
    });
  };

  const handleViewMedia = (url: string, index: number) => {
    setSelectedMediaUrl(url);
    setSelectedMediaIndex(index);
  };

  const handleBiographyCategorySelect = (categoryId: string, categoryName: string) => {
    setSelectedBiographyCategory({ id: categoryId, name: categoryName });
  };

  const handleBackToBiographyCategories = () => {
    setSelectedBiographyCategory(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <MemorialHeader isPreview={isPreview} onBackToAdmin={handleBackToAdmin} />
        <LoadingHeroSection />
        <main className="max-w-4xl mx-auto px-4 md:px-6 flex-grow py-0 pt-0 mt-0">
          <LoadingTabs />
        </main>
        <Footer />
      </div>
    );
  }

  // Show unassigned B2B message if needed (public scan of unassigned B2B medallion)
  if (showB2BEmailForm && memorialId) {
    return <UnassignedB2BMessage />;
  }

  return <MemorialProvider>
      <div className="min-h-screen bg-white flex flex-col">
        <DynamicMetaTags />
        <MemorialMetaTags />
        <MemorialHeader 
          isPreview={isPreview} 
          onBackToAdmin={handleBackToAdmin}
          showFixHeadstone={isB2B && offersRepairs}
          onReportIssue={() => setFixDialogOpen(true)}
        />

        <HeroSection 
          heroType={memorialData?.memorial?.hero_type} 
          themeId={memorialData?.memorial?.theme_id} 
        />

        <main id="life-journey" className="relative z-20 max-w-4xl mx-auto px-2 sm:px-4 md:px-6 md:py-0 flex-grow py-0 pt-0 mt-0 mb-8 overflow-x-hidden w-full">
          <MemorialTabs
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            startSlideshow={startSlideshow}
            galleryMedia={galleryMedia}
            isLoading={isMediaDataLoading}
            onViewMedia={handleViewMedia}
            memorialId={memorialId}
            selectedBiographyCategory={selectedBiographyCategory}
            onBiographyCategorySelect={handleBiographyCategorySelect}
            onBackToBiographyCategories={handleBackToBiographyCategories}
            monumentCompanyId={monumentCompanyId}
          />
        </main>

        <Footer />

        {/* Spacer to allow scrolling past footer when service banner is present */}
        {!isPreview && hasGeolocation && (
          <div className="h-20" aria-hidden="true" />
        )}

        {galleryMedia.length > 0 &&
          <PhotoSlideshow 
            isOpen={isSlideshow} 
            onClose={closeSlideshow} 
            photos={galleryMedia} 
            currentSlide={currentSlide} 
            onPrevious={handlePrevious} 
            onNext={handleNext} 
          />
        }
        
        {selectedMediaUrl && 
          <MediaViewer 
            media={galleryMedia} 
            onClose={() => setSelectedMediaUrl(null)} 
            initialIndex={selectedMediaIndex}
          />
        }

        {/* Floating footer CTA for services - only on public view and if memorial has geolocation */}
        {PURCHASING_ENABLED && !isPreview && hasGeolocation && (
          <MemorialServiceFooter
            onSendFlowers={() => {
              setSelectedServiceType("flowers");
              setServiceDialogOpen(true);
            }}
            onHeadstoneServices={() => {
              setSelectedServiceType("cleaning");
              setServiceDialogOpen(true);
            }}
          />
        )}

        {/* Service Order Dialog */}
        {PURCHASING_ENABLED && (
          <ServiceOrderDialog
            open={serviceDialogOpen}
            onOpenChange={setServiceDialogOpen}
            memorialId={memorialId || ""}
            serviceType={selectedServiceType}
            memorialName={memorialData?.memorial ? buildMemorialDisplayName(memorialData.memorial) : undefined}
          />
        )}

        {/* Order Success Dialog */}
        <OrderSuccessDialog
          open={orderSuccessOpen}
          onOpenChange={setOrderSuccessOpen}
          orderId={successOrderId}
          memorialName={memorialData?.memorial ? buildMemorialDisplayName(memorialData.memorial) : undefined}
        />

        {/* Fix Request Dialog for B2B memorials */}
        {monumentCompanyId && (
          <FixRequestDialog
            open={fixDialogOpen}
            onOpenChange={setFixDialogOpen}
            memorialId={memorialId || ""}
            monumentCompanyId={monumentCompanyId}
            memorialName={memorialData?.memorial ? buildMemorialDisplayName(memorialData.memorial) : undefined}
          />
        )}
      </div>
    </MemorialProvider>;
};

export default Memorial;
